import { BookOpen, CheckCircle2, Sparkles, X } from 'lucide-react'
import CustomModal from './CustomModal'
import type { CurriculumEntry } from '../../data/cohortCurriculum'

interface CurriculumModalProps {
  entry: CurriculumEntry
  packageTitle?: string
  onClose: () => void
}

export default function CurriculumModal({ entry, packageTitle, onClose }: CurriculumModalProps) {
  const introBody = entry.intro.filter((line) => line !== 'Dear Parent,')
  const closingBody = entry.closing.filter(
    (line) => line !== 'Best Regards,' && line !== 'Best Regards' && line !== 'RYD Learning Team',
  )

  return (
    <CustomModal
      modalStyle="relative flex max-h-[min(92dvh,760px)] w-full flex-col overflow-hidden rounded-[16px] bg-white sm:max-w-lg md:max-w-2xl"
      closeModal={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-ryd-primary via-[#9c2788] to-[#6b115f] px-4 py-4 text-white sm:px-5 sm:py-5">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 sm:right-4 sm:top-4 sm:h-9 sm:w-9"
            aria-label="Close curriculum"
          >
            <X size={16} className="sm:hidden" />
            <X size={18} className="hidden sm:block" />
          </button>
          <div className="flex items-start gap-3 pr-9 sm:pr-10">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/15 sm:h-10 sm:w-10">
              <BookOpen size={18} className="sm:hidden" />
              <BookOpen size={20} className="hidden sm:block" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/75 sm:text-[11px]">Curriculum</p>
              <h2 className="mt-0.5 font-[AvertaStd-Semibold] text-base leading-snug sm:text-lg md:text-xl">
                {entry.title}
              </h2>
              <p className="mt-1.5 flex flex-wrap gap-x-1.5 gap-y-0.5 text-[11px] text-white/85 sm:text-xs">
                <span className="rounded-full bg-white/15 px-2 py-0.5">{entry.level}</span>
                <span className="rounded-full bg-white/15 px-2 py-0.5">{entry.track}</span>
                {packageTitle ? (
                  <span className="rounded-full bg-white/10 px-2 py-0.5">{packageTitle}</span>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-y-contain px-4 py-4 sm:px-5 sm:py-5">
          <section>
            <h3 className="text-sm font-bold uppercase tracking-wide text-[#8b9aab]">Overview</h3>
            <p className="mt-2 text-[14px] font-semibold leading-snug text-ryd-subTextPrimary sm:text-[15px]">{entry.subject}</p>
            <div className="mt-3 space-y-2 text-[13px] leading-relaxed text-[#576877] sm:text-[14px]">
              {introBody.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-[#ebe4f0] bg-[#faf8fb] p-3 sm:p-4">
            <h3 className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide text-[#8b9aab] sm:text-sm">
              <CheckCircle2 size={15} className="text-ryd-primary" />
              Learning objectives
            </h3>
            <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-x-4 sm:gap-y-2.5">
              {entry.learningObjectives.map((objective) => (
                <li
                  key={objective}
                  className="flex items-start gap-2 text-[13px] leading-snug text-[#475569] sm:text-[14px]"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-ryd-primary" />
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-3 sm:p-4">
            <h3 className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide text-indigo-800 sm:text-sm">
              <Sparkles size={15} />
              Capstone project
            </h3>
            <p className="mt-2 text-[14px] font-semibold text-indigo-950 sm:text-[15px]">{entry.project.name}</p>
            <p className="mt-2 text-[13px] leading-relaxed text-indigo-900/80 sm:text-[14px]">{entry.project.description}</p>
          </section>

          {closingBody.length > 0 ? (
            <section className="rounded-xl border border-dashed border-[#e2d8e8] bg-white px-4 py-3 text-[13px] leading-relaxed text-[#64748b] sm:text-[14px]">
              {closingBody.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </section>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-[#f0eaf0] bg-white px-4 py-3 sm:px-5 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl bg-ryd-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#701466] sm:py-3"
          >
            Close
          </button>
        </div>
      </div>
    </CustomModal>
  )
}
